import { BooleanThreePartInput, StreetAddress } from 'API';
import { isValidEmail } from 'shared/utilities';

interface StreetAddressInput {
  isUS?: boolean;
  requireCountry?: boolean;
}

interface IMoney {
  amount?: number;
  currency?: string
}

class FormService {
  streetAddressPrimaryValidator = (
    input?: StreetAddressInput,
  ) => (value: StreetAddress) => {
    const isUS = input?.isUS;
    const requireCountry = input?.requireCountry;

    const isBlank = Boolean(!value?.address
      && !value?.adminArea
      && !value?.careOf
      && !value?.city
      && !value?.postalCode
      && !value?.state
      && (!value?.country || isUS));

    const isValid = value?.address
      && value?.city
      && (value?.adminArea || value?.state)
      && value?.postalCode
      && (value?.unitNumber || (!value?.unitNumber && !value?.unitType))
      && (value?.country || !requireCountry);

    const isEmpty = Boolean(!value?.address?.trim()
    || !value?.city?.trim()
    || !value?.adminArea?.trim()
    || !value?.postalCode?.trim());

    if (isBlank || !isValid || isEmpty) {
      return this.errors.invalidAddress;
    }

    return '';
  };

  streetAddressValidator = (
    input?: StreetAddressInput,
  ) => (value: StreetAddress) => {
    const isUS = input?.isUS;
    const requireCountry = input?.requireCountry;

    const isBlank = Boolean(!value?.address
      && !value?.adminArea
      && !value?.careOf
      && !value?.city
      && !value?.postalCode
      && !value?.state
      && (!value?.country || isUS));

    const isValid = value?.address
      && value?.city
      && (value?.adminArea || value?.state)
      && value?.postalCode
      && (value?.unitNumber || (!value?.unitNumber && !value?.unitType))
      && (value?.country || !requireCountry);

    if (isBlank || isValid) {
      return '';
    }

    return this.errors.invalidAddress;
  };

  emailValidator = (value: string) => {
    if (!value) {
      return '';
    }

    return isValidEmail(value) ? '' : this.errors.invalidEmail;
  };

  allowsEmployeesToTelecommute = (value: BooleanThreePartInput) => {
    if (!value.value) {
      return '';
    }

    if (value.followValueSecond === null || value.followValue === null) {
      return this.errors.inValidFillOption;
    }

    return '';
  };

  errors = {
    invalidAddress: 'Address is incomplete.',
    invalidDigit9: 'Must contain nine digits.',
    invalidEmail: 'Must be valid email address.',
    invalidPhone: 'Must contain at least 10 digits.',
    invalidSalary: 'This salary is not a valid amount.',
    invalidYear: 'Must be a valid year.',
    requiredField: 'This is a required field.',
    invalidId: 'Employee ID cannot be greater than 25 charachters',
    invalidIdFormat: 'Employee ID cannot contain special charachters',
    invalidNumber: 'Must be less than 8 digits',
    inValidFillOption: 'Must be fill both questions',
  };

  employeeIdValidator = (value: string) => {
    if (!value) {
      return '';
    }
    // eslint-disable-next-line
    const specialChars = value.match(/[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/);
    if (value.length > 25) {
      return this.errors.invalidId;
    }
    if (specialChars) {
      return this.errors.invalidIdFormat;
    }

    return '';
  };

  employeeValidator = (value: number) => {
    if (value === null) {
      return this.errors.requiredField;
    }

    const employee = String(value);

    return employee && employee.length < 8 ? '' : this.errors.invalidNumber;
  };

  moneyValidator = (value: IMoney) => {
    const { amount } = value;
    // eslint-disable-next-line no-restricted-globals, no-bitwise
    if (amount != null && isNaN(amount)) {
      return this.errors.requiredField;
    }

    return '';
  };

  emptyValidator = (value: String) => {
    if (!value.trim()) {
      return this.errors.requiredField;
    }

    return '';
  };

  emptyArrayValidator = (value: any) => {
    if (!value.length) {
      return this.errors.requiredField;
    }

    return '';
  };

  phoneValidator = (value: string) => {
    if (!value) {
      return '';
    }

    const digits = value.match(/\d/g);

    return digits && digits.length >= 10 ? '' : this.errors.invalidPhone;
  };

  digit9Validator = (value: string) => {
    if (!value) {
      return '';
    }

    const digits = value.match(/\d/g);

    return digits && digits.length === 9 ? '' : this.errors.invalidDigit9;
  };

  yearValidator = (value: string) => {
    if (!value) {
      return '';
    }

    const digits = value.toString().match(/\d/g);

    return value.toString().length === 4 && digits?.length === 4
      ? ''
      : this.errors.invalidYear;
  };
}

export default new FormService();
